/**
 * GamifyPage — landing page for the puzzle maze feature ("Gamify ✨").
 *
 * Shows a grid of subject tiles. Math is live with topic+difficulty pickers
 * that launch the maze. Other subjects are "Coming soon" (still rendered so
 * the user feels the breadth of what's coming).
 *
 * Tier gate: backend returns `eligible=false` for basic / basic_lifetime
 * plans. Clicking any topic on those plans surfaces the upgrade modal.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Sparkles, Calculator, Atom, FlaskConical, Leaf, Brain, Trophy,
  Lock, ArrowRight, Loader2, Star, Zap, Target, Clock,
  BookOpen, Globe, Landmark, Map, Monitor,
} from 'lucide-react';
import { toast } from 'sonner';
import CoinShopDialog from '@/components/premium/CoinShopDialog';
import ActiveScopeChip from '@/components/ActiveScopeChip';
import FeedbackModal from '@/components/FeedbackModal';

const SUBJECT_ICON = {
  math:           { icon: Calculator,   gradient: 'from-orange-400 via-pink-400 to-rose-500',          ring: 'ring-orange-200',  accent: 'text-rose-600' },
  physics:        { icon: Atom,         gradient: 'from-blue-400 via-cyan-400 to-teal-500',            ring: 'ring-blue-200',    accent: 'text-blue-600' },
  chemistry:      { icon: FlaskConical, gradient: 'from-violet-400 via-purple-500 to-indigo-600',      ring: 'ring-violet-200',  accent: 'text-violet-600' },
  biology:        { icon: Leaf,         gradient: 'from-green-400 via-emerald-500 to-teal-600',        ring: 'ring-emerald-200', accent: 'text-emerald-600' },
  english:        { icon: BookOpen,     gradient: 'from-amber-300 via-orange-400 to-rose-500',         ring: 'ring-amber-200',   accent: 'text-orange-600' },
  hindi:          { icon: BookOpen,     gradient: 'from-fuchsia-400 via-pink-400 to-rose-500',         ring: 'ring-fuchsia-200', accent: 'text-fuchsia-600' },
  social_studies: { icon: Globe,        gradient: 'from-sky-400 via-indigo-500 to-purple-600',         ring: 'ring-sky-200',     accent: 'text-sky-600' },
  history:        { icon: Landmark,     gradient: 'from-stone-400 via-amber-600 to-orange-700',        ring: 'ring-amber-200',   accent: 'text-amber-700' },
  geography:      { icon: Map,          gradient: 'from-emerald-400 via-teal-500 to-cyan-600',         ring: 'ring-teal-200',    accent: 'text-teal-600' },
  computer:       { icon: Monitor,      gradient: 'from-slate-500 via-indigo-500 to-violet-600',       ring: 'ring-slate-300',   accent: 'text-indigo-600' },
  logic:          { icon: Brain,        gradient: 'from-amber-400 via-orange-500 to-red-500',          ring: 'ring-amber-200',   accent: 'text-amber-600' },
};

const DIFFICULTY_META = {
  easy:   { label: 'Easy',   blurb: '6 cells · light hops',  emoji: '🌱', stars: 1 },
  medium: { label: 'Medium', blurb: '8 cells · think clearly', emoji: '🚀', stars: 2 },
  hard:   { label: 'Hard',   blurb: '10 cells · level boss',  emoji: '🔥', stars: 3 },
};

export default function GamifyPage() {
  const navigate = useNavigate();
  const [shopOpen, setShopOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [pickerSubject, setPickerSubject] = useState(null);
  const [pickerTopic, setPickerTopic] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  // Iter 60e (Jul 2026) — react to top-bar dropdown changes AT RUNTIME.
  // Previously we snapshotted on mount, so switching Subject/Topic on the
  // top bar didn't refresh the puzzle list. Now we listen to storage
  // events + poll on visibility change so the Gamify page always reflects
  // the current filter.
  const readScope = () => {
    try {
      return {
        subject: window.localStorage.getItem('gv_active_subject') || '',
        topic: window.localStorage.getItem('gv_active_topic') || '',
      };
    } catch { return { subject: '', topic: '' }; }
  };
  const [scopeSubject, setScopeSubject] = useState(() => readScope().subject);
  const [scopeTopic, setScopeTopic] = useState(() => readScope().topic);
  // Iter 60f (Jul 2026) — Empty-state feedback funnel now opens the shared
  // Feedback modal (Bhalu / preferred buddy) instead of routing away to
  // /chat. Pre-seeds the scope so the student only types the puzzle idea.
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    const sync = () => {
      const s = readScope();
      setScopeSubject(prev => (prev === s.subject ? prev : s.subject));
      setScopeTopic(prev => (prev === s.topic ? prev : s.topic));
    };
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    // Same-tab updates from WebLayout don't fire the storage event — poll gently.
    const id = window.setInterval(sync, 800);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (scopeSubject) params.set('subject', scopeSubject);
    const qs = params.toString() ? `?${params.toString()}` : '';
    setLoading(true);
    Promise.all([
      apiGet(`/puzzles/subjects${qs}`),
      apiGet('/puzzles/my-stats').catch(() => null),
      apiGet(`/puzzles/daily-challenge${qs}`).catch(() => null),
    ])
      .then(([subjectsRes, statsRes, dailyRes]) => {
        setData(subjectsRes);
        setStats(statsRes);
        setDaily(dailyRes);
      })
      .catch(err => toast.error(err?.message || 'Could not load puzzles.'))
      .finally(() => setLoading(false));
  }, [scopeSubject, scopeTopic]);

  const eligible = data?.eligible;

  const handleSubjectClick = (subj) => {
    // Iter 60 (Jul 2026): if a subject has no syllabus-derived topics yet
    // (admin hasn't uploaded chapters), show a helpful nudge instead of
    // silently falling back to a canned list.
    if (!subj.live || !subj.topics || subj.topics.length === 0) {
      toast.info(subj.coming_soon_blurb || `${subj.label} puzzles are coming soon!`);
      return;
    }
    if (!eligible) {
      setShowUpgrade(true);
      return;
    }
    setPickerSubject(subj);
    // Iter 60t (Aug 2026) — preselect the currently-scoped Chapter (from
    // the top-bar dropdown) so the puzzle is generated for the chapter
    // the student is actually studying right now, not just the first
    // chapter in the list. Falls back to the first topic if no match.
    const scopedTopicLower = (scopeTopic || '').trim().toLowerCase();
    const matched = scopedTopicLower
      ? subj.topics.find(t => (t.label || '').trim().toLowerCase() === scopedTopicLower)
      : null;
    setPickerTopic(matched || subj.topics[0]);
  };

  const handleStart = async (difficulty) => {
    if (!eligible) { setShowUpgrade(true); return; }
    if (!pickerSubject || !pickerTopic) return;
    setGenerating(true);
    try {
      // Variant rotation respects tier-based cap (basic=0/blocked, growth=1, elite=25).
      // The FE rotates 1..cap; backend also clamps server-side as a safety net.
      const cap = Math.max(1, parseInt(data?.variant_cap || 1, 10));
      const variantKey = `gv_pz_var_${pickerSubject.id}_${pickerTopic.id}_${difficulty}`;
      let variant = 1;
      try {
        const stored = parseInt(window.localStorage.getItem(variantKey) || '0', 10) || 0;
        variant = (stored % cap) + 1;
        window.localStorage.setItem(variantKey, String(variant));
      } catch { /* localStorage unavailable */ }
      const res = await apiPost('/puzzles/generate', {
        subject: pickerSubject.id,
        topic: pickerTopic.id,
        class_level: data.class_level,
        difficulty,
        variant,
      });
      const pid = res?.puzzle?.puzzle_id;
      if (!pid) throw new Error('No puzzle id returned.');
      // Iter 60v (Aug 2026) — Never surface cache/replay state to students.
      // Puzzles are shared across all students by design (same subject +
      // chapter + difficulty = same board). The FE just navigates —
      // students see it as freshly-crafted content every time.
      navigate(`/gamify/play/${pid}`);
    } catch (err) {
      const msg = (err?.message || '').replace(/^HTTP \d+:\s*/, '');
      if (err?.message?.includes('upgrade_required') || err?.status === 403) {
        setShowUpgrade(true);
      } else {
        toast.error(msg || 'Could not start the puzzle.');
      }
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-400" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="gamify-page">
      <CoinShopDialog open={shopOpen} onOpenChange={setShopOpen} />
      {/* Phase 2.70 — chapter-scope chip */}
      <ActiveScopeChip subject={scopeSubject} topic={scopeTopic} onClear={() => {
        try {
          window.localStorage.removeItem('gv_active_subject');
          window.localStorage.removeItem('gv_active_topic');
        } catch { /* localStorage unavailable */ }
        setScopeSubject('');
        setScopeTopic('');
      }} />

      {/* Hero — Iter 60e: copy now adapts to the active subject */}
      <Card className="relative overflow-hidden p-8 bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 border-rose-200 shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-pink-200/40 to-orange-200/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-amber-200 to-pink-200 text-amber-900 text-[11px] font-heading uppercase tracking-wider shadow-sm">
              <Sparkles className="w-3 h-3" /> Gamify
            </span>
            {!eligible && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-700 text-[11px] font-heading">
                <Lock className="w-3 h-3" /> Growth & Elite only
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800">
            {scopeSubject ? (
              <>Bite through <span className="bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">{scopeSubject}</span>, one puzzle at a time.</>
            ) : (
              <>Turn every chapter into a <span className="bg-gradient-to-r from-rose-500 to-orange-500 bg-clip-text text-transparent">playground.</span></>
            )}
          </h1>
          <p className="mt-3 text-base font-body text-slate-600 max-w-2xl leading-relaxed">
            Solve a problem, hop on the matching answer-strip, repeat — until you reach the treasure. Every right click feels like a mini-celebration. Every wrong click is just another chance to think it through.
          </p>
          {stats && stats.completed > 0 && (
            <div className="mt-5 flex flex-wrap gap-3" data-testid="gamify-stats">
              <StatPill icon={Trophy} label="Completed" value={stats.completed} accent="amber" />
              <StatPill icon={Star} label="Perfects" value={stats.perfects} accent="rose" />
              <StatPill icon={Zap} label="Veda Coins" value={stats.puzzle_xp} accent="violet" />
              <Button size="sm" variant="outline" className="h-8 text-[11px] self-center"
                data-testid="open-coin-shop" onClick={() => setShopOpen(true)}>
                Spend Veda Coins
              </Button>
            </div>
          )}
          {eligible && data?.variant_cap > 0 && (
            <p className="mt-4 text-xs font-body text-slate-500" data-testid="gamify-variant-cap">
              You&apos;re on the <span className="font-heading text-slate-700">{(data.tier || '').toUpperCase()}</span> plan — dive into as many puzzles as you like. Every chapter&apos;s ready for you.
            </p>
          )}
        </div>
      </Card>

      {/* Daily Challenge ribbon — hidden when the top-bar subject filter
          is set and the challenge belongs to a different subject.
          Prevents the confusing "Arts is selected but Daily = Chemistry"
          state the user reported (iter 60e). */}
      {daily?.puzzle?.puzzle_id && (!scopeSubject || (daily.subject_label || '').toLowerCase() === scopeSubject.toLowerCase()) && (
        <Card data-testid="daily-challenge-ribbon"
          className={`p-5 bg-gradient-to-r from-indigo-50 via-violet-50 to-fuchsia-50 border-2 border-violet-200 shadow-md relative overflow-hidden ${daily.already_completed_today ? 'opacity-80' : ''}`}>
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-violet-200/30 rounded-full blur-2xl pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg flex-shrink-0">
              <Sparkles className="w-7 h-7 text-white" strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-600 text-white text-[10px] font-heading uppercase tracking-wider shadow">
                  Daily Challenge
                </span>
                <span className="text-[11px] font-body text-slate-500">{daily.date}</span>
                {daily.already_completed_today && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-heading">
                    <Trophy className="w-3 h-3" /> Done today
                  </span>
                )}
              </div>
              <h3 className="font-display text-lg font-bold text-slate-800">
                {daily.subject_label} · {daily.topic_label}
                <span className="ml-2 text-xs font-body text-slate-500 font-normal capitalize">({daily.difficulty})</span>
              </h3>
              <p className="text-xs font-body text-slate-600 mt-1">
                {daily.already_completed_today
                  ? "Come back tomorrow for a fresh challenge — bonus Veda Coins refresh at midnight IST."
                  : `Play today's curated puzzle for bonus Veda Coins. Win up to `}
                {!daily.already_completed_today && (
                  <span className="font-heading text-violet-700">+{daily.bonus_xp_total} Veda Coins</span>
                )}
                {!daily.already_completed_today && ` (${daily.bonus_multiplier}× multiplier).`}
              </p>
            </div>
            <Button
              disabled={dailyLoading || daily.already_completed_today}
              onClick={async () => {
                if (daily.already_completed_today) return;
                setDailyLoading(true);
                // Navigate with a query flag so the play page knows to complete via daily endpoint
                navigate(`/gamify/play/${daily.puzzle.puzzle_id}?daily=1`);
              }}
              data-testid="daily-challenge-play-btn"
              className="font-heading text-xs bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white shadow-md disabled:opacity-50">
              {daily.already_completed_today ? 'Done' : (
                <>Play <ArrowRight className="w-3.5 h-3.5 ml-1" /></>
              )}
            </Button>
          </div>
        </Card>
      )}

      {/* Subject grid */}
      {(() => {
        const allSubjects = data?.subjects || [];
        const liveSubjects = allSubjects.filter(s => s.live && (s.topics?.length || 0) > 0);
        const hasAnyLive = liveSubjects.length > 0;
        if (!hasAnyLive) {
          // Iter 60e — friendly empty state when NO subject in the current
          // scope has syllabus-derived puzzle topics yet. Uses the same
          // warm palette as the hero + funnels the student into leaving
          // feedback so they feel heard.
          return (
            <Card
              data-testid="gamify-empty-state"
              className="relative overflow-hidden p-8 sm:p-10 bg-gradient-to-br from-indigo-50 via-violet-50 to-pink-50 border-2 border-dashed border-violet-200 text-center"
            >
              <div className="absolute -top-8 -right-8 w-40 h-40 bg-violet-200/30 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-pink-200/30 rounded-full blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[11px] font-heading uppercase tracking-widest shadow mb-4">
                  <Sparkles className="w-3 h-3" /> Coming Soon
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
                  {scopeSubject
                    ? <>Puzzles for <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">{scopeSubject}</span> are being crafted for you.</>
                    : <>Puzzles are being crafted for your chapters.</>}
                </h3>
                <p className="font-body text-sm sm:text-base text-slate-600 max-w-xl mx-auto leading-relaxed">
                  Your voice matters — tell us what kind of puzzle would help you crack {scopeTopic || scopeSubject || 'this chapter'} the most, and we&apos;ll build it into the next release.
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <button
                    data-testid="gamify-feedback-btn"
                    onClick={() => setFeedbackOpen(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-heading shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:scale-95 transition-all"
                  >
                    <Sparkles className="w-4 h-4" /> Share what you&apos;d love to play
                  </button>
                  {scopeSubject && (
                    <button
                      data-testid="gamify-clear-scope-btn"
                      onClick={() => {
                        try {
                          window.localStorage.removeItem('gv_active_subject');
                          window.localStorage.removeItem('gv_active_topic');
                        } catch { /* localStorage unavailable */ }
                        setScopeSubject('');
                        setScopeTopic('');
                      }}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-slate-700 text-xs font-heading border border-slate-200 hover:border-violet-300 transition-colors"
                    >
                      See all subjects
                    </button>
                  )}
                </div>
              </div>
            </Card>
          );
        }
        return (
          <div>
            <h2 className="font-heading text-base text-slate-500 uppercase tracking-wide mb-3">Pick your battlefield</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {allSubjects.map(subj => {
                const meta = SUBJECT_ICON[subj.id] || SUBJECT_ICON.logic;
                const Icon = meta.icon;
            const locked = !subj.live || !eligible;
            return (
              <button
                key={subj.id}
                onClick={() => handleSubjectClick(subj)}
                data-testid={`subject-tile-${subj.id}`}
                className={`group relative text-left rounded-3xl overflow-hidden transition-all duration-300 ${
                  subj.live ? 'hover:scale-[1.02] hover:shadow-2xl' : 'opacity-90 hover:opacity-100'
                } ${meta.ring} ring-1 hover:ring-2`}
              >
                <div className={`bg-gradient-to-br ${meta.gradient} p-5 h-44 relative`}>
                  <div className="absolute inset-0 opacity-30 mix-blend-soft-light"
                    style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.6) 0%, transparent 60%)' }} />
                  <div className="relative flex items-start justify-between h-full">
                    <div>
                      <Icon className="w-12 h-12 text-white drop-shadow-md mb-2 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                      <h3 className="font-display text-2xl font-bold text-white drop-shadow">{subj.label}</h3>
                      <p className="text-xs font-body text-white/85 mt-1 max-w-[200px] leading-snug">
                        {subj.live
                          ? (subj.topics[0]?.blurb || `${subj.topics.length} topic${subj.topics.length > 1 ? 's' : ''} ready`)
                          : (subj.coming_soon_blurb || 'Coming soon')}
                      </p>
                    </div>
                    {subj.excluded_from_puzzles ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-slate-600 text-[10px] font-heading shadow" data-testid={`subject-tile-${subj.id}-excluded-pill`}>
                        Try Quiz
                      </span>
                    ) : !subj.live ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-slate-700 text-[10px] font-heading shadow">
                        <Clock className="w-3 h-3" /> SOON
                      </span>
                    ) : !eligible ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-amber-700 text-[10px] font-heading shadow">
                        <Lock className="w-3 h-3" /> LOCKED
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-emerald-700 text-[10px] font-heading shadow">
                        <Sparkles className="w-3 h-3" /> PLAY
                      </span>
                    )}
                  </div>
                </div>
                {subj.live ? (
                  <div className="bg-white/95 backdrop-blur p-3 flex items-center justify-between">
                    <span className="text-[11px] font-body text-slate-500">{subj.topics.length} chapters</span>
                    <span className={`text-[11px] font-heading ${meta.accent} flex items-center gap-1`}>
                      Choose chapter <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                ) : subj.excluded_from_puzzles ? (
                  <div className="bg-white/95 backdrop-blur p-3 flex items-center justify-between" data-testid={`subject-tile-${subj.id}-excluded-footer`}>
                    <span className="text-[11px] font-body text-slate-500">Puzzles aren&apos;t a fit here</span>
                    <span className="text-[11px] font-heading text-slate-500 flex items-center gap-1">
                      Try Quiz &amp; Flashcards <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                ) : (
                  <></>
                )}
              </button>
            );
          })}
        </div>
      </div>
        );
      })()}

      {/* Topic + Difficulty picker */}
      <Dialog open={Boolean(pickerSubject)} onOpenChange={(v) => !v && setPickerSubject(null)}>
        <DialogContent
          className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0"
          data-testid="puzzle-picker"
        >
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
            <DialogTitle className="font-display text-xl">Pick a chapter + difficulty</DialogTitle>
            <DialogDescription className="font-body text-xs">
              Pick a chapter, choose your difficulty, and hop through the answer-strips to reach the treasure.
            </DialogDescription>
          </DialogHeader>

          {pickerSubject && (
            <div
              data-testid="puzzle-picker-body"
              className="flex-1 min-h-0 overflow-y-auto px-6 pb-6 space-y-4"
            >
              <div>
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <p className="text-[11px] font-heading text-slate-500 uppercase">Chapter</p>
                  {scopeTopic && pickerTopic && (pickerTopic.label || '').trim().toLowerCase() === scopeTopic.trim().toLowerCase() && (
                    <span
                      data-testid="picker-scope-hint"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-heading"
                    >
                      <Sparkles className="w-3 h-3" /> Auto-selected from your active chapter
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[35vh] overflow-y-auto pr-1">
                  {pickerSubject.topics.map(t => (
                    <button key={t.id} onClick={() => setPickerTopic(t)}
                      data-testid={`picker-topic-${t.id}`}
                      className={`text-left px-4 py-3 rounded-xl border-2 transition-all ${
                        pickerTopic?.id === t.id
                          ? 'border-rose-400 bg-rose-50 shadow'
                          : 'border-slate-200 hover:border-rose-200 bg-white'
                      }`}>
                      <p className="font-heading text-sm text-slate-800">{t.label}</p>
                      <p className="text-[11px] font-body text-slate-500 mt-0.5">{t.blurb}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[11px] font-heading text-slate-500 uppercase mb-2">Difficulty</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {Object.entries(DIFFICULTY_META).map(([key, meta]) => (
                    <button key={key} onClick={() => handleStart(key)} disabled={generating}
                      data-testid={`picker-difficulty-${key}`}
                      className="text-left px-3 py-3 rounded-xl border-2 border-slate-200 hover:border-orange-300 hover:bg-orange-50/50 transition-all bg-white disabled:opacity-50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-heading text-sm text-slate-800">{meta.label}</span>
                        <span className="text-base">{meta.emoji}</span>
                      </div>
                      <div className="flex items-center gap-0.5 mb-1">
                        {[1,2,3].map(n => (
                          <Star key={n} className={`w-3 h-3 ${n <= meta.stars ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                        ))}
                      </div>
                      <p className="text-[10px] font-body text-slate-500">{meta.blurb}</p>
                    </button>
                  ))}
                </div>
                {generating && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-sm text-slate-500 font-body" data-testid="puzzle-generating">
                    <Loader2 className="w-4 h-4 animate-spin" /> Crafting your maze…
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upgrade modal for basic / basic_lifetime */}
      <Dialog open={showUpgrade} onOpenChange={setShowUpgrade}>
        <DialogContent className="sm:max-w-md" data-testid="gamify-upgrade-modal">          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-rose-500" /> Unlock Gamify
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-3">
            <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-rose-400 via-orange-400 to-amber-400 mx-auto flex items-center justify-center shadow-lg mb-4">
              <Target className="w-10 h-10 text-white" strokeWidth={1.5} />
            </div>
            <p className="font-body text-slate-700 leading-relaxed max-w-sm mx-auto">
              Gamify puzzles are part of <span className="font-heading text-rose-600">Growth</span> &
              {' '}<span className="font-heading text-violet-600">Elite</span> plans. Upgrade to start hopping
              through Math, plus more subjects coming soon.
            </p>
            <Button onClick={() => navigate('/subscription')} data-testid="gamify-upgrade-btn"
              className="mt-5 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white font-heading">
              See plans <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter 60f — Feedback modal (Bhalu / preferred buddy). Wired from
          the "Coming Soon" empty-state CTA so students share their puzzle
          ideas without leaving the page. Pre-seeded with the active
          Subject → Topic scope so they only type the actual idea. */}
      <FeedbackModal
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        initialMessage={
          scopeSubject || scopeTopic
            ? `Puzzle idea for ${scopeSubject}${scopeTopic ? ` → ${scopeTopic}` : ''}:\n\n`
            : ''
        }
      />
    </div>
  );
}

function StatPill({ icon: Icon, label, value, accent }) {
  const map = {
    amber:  'from-amber-100 to-amber-50 text-amber-700 border-amber-200',
    rose:   'from-rose-100 to-rose-50 text-rose-700 border-rose-200',
    violet: 'from-violet-100 to-violet-50 text-violet-700 border-violet-200',
  };
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r border ${map[accent] || map.amber}`}>
      <Icon className="w-3.5 h-3.5" />
      <span className="text-[11px] font-heading uppercase tracking-wide opacity-80">{label}</span>
      <span className="font-display text-base font-bold">{value}</span>
    </div>
  );
}
